import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Field } from "formik";
import FormFieldError from "components/form/form-field-error";
import { formikPropTypes } from "components/common";
import withStyles from "components/with-styles";
import styles from "./styles/form-videofield.css.js";
import NoImage from "static/images/no-image.png";
import { injectIntl } from "react-intl";
import FormHelperText from "components/form/form-helper-text";

const ValidatedVideoField = ({
  field: { name, value, onChange, onBlur },
  id,
  label,
  accept = "video/*",
  disabled,
  intl,
  fileForPreview,
}) => {
  const inputVideoRef = useRef(null);

  return (
    <>
      <label style={{ fontSize: 14, marginRight: 20 }} htmlFor={id}>
        {label}
      </label>
      <input
        type="button"
        value={
          fileForPreview
            ? intl.formatMessage({ id: "common.asset.modify" })
            : intl.formatMessage({ id: "common.asset.upload" })
        }
        onClick={e => inputVideoRef.current.click()}
      />
      <input
        style={{ visibility: "hidden" }}
        ref={inputVideoRef}
        onChange={e => onChange(e.target.files[0])}
        type="file"
        id={id}
        name={name}
        disabled={disabled}
        value={value}
        accept={accept}
      />
      <FormFieldError name={name} />
    </>
  );
};

ValidatedVideoField.propTypes = {
  ...formikPropTypes,
  /**
   * Id del input
   */
  id: PropTypes.string,
  /**
   * Etiqueta del input
   */
  label: PropTypes.string,
  /**
   * Tipo MIME de imágenes que acepta
   */
  accept: PropTypes.string,
};

const I18nValidatedVideoField = injectIntl(ValidatedVideoField);

const FormVideoField = ({ name, onChange, value, onBlur, fileForPreview, classes, intl, ...props }) => {
  const videoPreviewRef = useRef();
  const handleChange = value => onChange(name)(value);

  useEffect(
    () => {
      if (videoPreviewRef.current) {
        videoPreviewRef.current.load();
      }
    },
    [fileForPreview],
  );

  return (
    <>
      <Field
        component={I18nValidatedVideoField}
        field={{
          name,
          onChange: handleChange,
          value,
          onBlur,
        }}
        name={name}
        fileForPreview={fileForPreview}
        {...props}
      />
      {!fileForPreview && <FormHelperText text={intl.formatMessage({ id: "common.asset.notFound" })} />}
      <div className={classes.preview}>
        {fileForPreview && (
          <video
            ref={videoPreviewRef}
            preload="none"
            style={{ maxWidth: "100%" }}
            controls
            onError={event => (event.target.src = NoImage)}
          >
            <source src={URL.createObjectURL(fileForPreview)} type="video/mp4" />
          </video>
        )}
      </div>
    </>
  );
};

FormVideoField.propTypes = {
  /**
   * Indica si acepta la subida de varias imágenes
   */
  multiple: PropTypes.bool,
  /**
   * Indica si se ve una preview del video seleccionado
   */
  videoPreview: PropTypes.bool,
  /**
   * Tipo MIME de imágenes que acepta
   */
  accept: PropTypes.string,
  /**
   * Callback que se dispara con el cambio del input
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Etiqueta del input
   */
  label: PropTypes.string,
  /**
   * Nombre del campo
   */
  name: PropTypes.string,
  /**
   * Valor por defecto del campo
   */
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  /**
   * Callback que se dispara con el cambio de foco
   */
  onBlur: PropTypes.func,
  /**
   * Fichero seleccionado por el form (para previsualización)
   */
  fileForPreview: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};

export default withStyles(styles)(injectIntl(FormVideoField));
